import React, { useEffect, useState } from "react";
import {
  Create,
  TextInput,
  SimpleForm,
  required,
  maxLength,
  number,
  regex,
  CreateProps,
  SelectInput,
  BooleanInput,
} from "react-admin";
import { useField } from "react-final-form";
import { Grid, TextField, Typography } from "@material-ui/core";

import CustomImageInput from "components/CustomImageInput";
import SanitizedGrid from "components/SanitizedGrid";
import TitleBreadcrumb from "components/TitleBreadcrumb";
import CreateToolbar from "components/CustomCreateToolbar";
import { emailValidation, mobilePhoneValidation } from "utils/validation";
import CustomAddressAutoCompleteInput from "./components/CustomAddressAutoCompleteInput";
import CustomOneMap from "./components/CustomOneMap";
import { areaCalculationType, pricingPolicyList } from "./const";

const SchoolCreate = (props: CreateProps) => {
  const [markerPosition, setMarker] = useState({ lat: "", lng: "" });

  const breadcrumb = [
    { title: "School", link: "/schools" },
    { title: "Create", link: "/schools/create" },
  ];

  const transform = async (data: any) => {
    if (data.photoUrl) {
      if (data.photoUrl.url) {
        data.photoUrl = (data.photoUrl.url as string).split(",")[1];

        delete data.photoUrlFile;
      }
    } else {
      data.photoUrl = null;
    }

    if (data.pricingPolicy === "contactDays") {
      data.isFullCalendarDaysCharge = false;
    } else {
      data.isFullCalendarDaysCharge = true;
    }

    delete data.pricingPolicy;

    return data;
  };

  const SchoolPrefixFormater = (data: string) => {
    if (data) {
      return data.toUpperCase();
    }
  };

  const callback = (value: { lat: string; lng: string }) => {
    setMarker({ lat: value.lat, lng: value.lng });
  };

  return (
    <>
      <TitleBreadcrumb paths={breadcrumb} />
      <Typography variant="h4">New School</Typography>
      <Create transform={transform} {...props}>
        <SimpleForm toolbar={<CreateToolbar />}>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">School Information</Typography>
            </Grid>

            <Grid item xs={4}>
              <TextInput
                label="School Name"
                source="name"
                validate={required()}
              />
            </Grid>

            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <TextInput
                label="Email Address"
                source="email"
                type="email"
                validate={emailValidation}
              />
            </Grid>

            <Grid item xs={4}>
              <TextInput
                label="Contact Number"
                source="phone"
                // regular Expression for TextInput doesnt have white spaces
                validate={mobilePhoneValidation}
              />
            </Grid>

            <Grid item xs={4}></Grid>

            <Grid item xs={5}>
              <CustomImageInput
                label="Profile image"
                source="photoUrl"
                fieldType="image"
                accept="image/*"
                placeholder="Drop a images (*.jpg/jpeg/png) to upload, or click to select it."
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">School Address</Typography>
            </Grid>

            <Grid item xs={4}>
              <CustomAddressAutoCompleteInput
                label="Postal Code *"
                source="postalCode"
                referenceSource="locationData"
                parentCallback={callback}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={2}>
              <TextInput label="Unit No." source="unitNumber" />
            </Grid>
            <Grid item xs={2}>
              <TextInput
                label="Block No."
                source="blockNumber"
                validate={required()}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <TextInput label="Building Name" source="building" />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <TextInput
                label="Address"
                source="address"
                validate={required()}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            {markerPosition.lat !== "" && markerPosition.lng !== "" && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6">
                    Map Location (Location for school's dropoff/pickup)
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <CustomOneMap referenceSource="locationData" />
                  <LatLngInput
                    lat={markerPosition.lat}
                    lng={markerPosition.lng}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Typography variant="h6">Other Details</Typography>
            </Grid>

            <Grid item xs={3}>
              <TextInput
                label="School Prefix"
                source="schoolPrefix"
                format={SchoolPrefixFormater}
                validate={[required(), regex(/^[A-Za-z]+$/), maxLength(6)]}
                placeholder="Enter Up to 6 Alphabets"
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <SelectInput
                source="areaDistanceCalculationType"
                label="Type for Student Area Distance Calculation"
                choices={areaCalculationType}
                validate={required()}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <SelectInput
                source="pricingPolicy"
                label="Pricing Policy"
                choices={pricingPolicyList}
                validate={required()}
              />
            </Grid>
            <Grid item xs={9}></Grid>
          </SanitizedGrid>
        </SimpleForm>
      </Create>
    </>
  );
};

const BoundedTextField = ({ name, label, val, onSet }: any) => {
  const {
    input: { onChange, value },
    meta: { touched, error },
  } = useField(name);

  useEffect(() => {
    onSet && onSet({ type: name, coordinate: value });
  }, []);

  useEffect(() => {
    onChange(val);
  }, [val, onChange]);

  return (
    <TextField name={name} label={label} onChange={onChange} value={val} />
  );
};

export const LatLngInput = ({ lat, lng, updateLocation }: any) => {
  const latLngStyle = {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "10px",
  };

  return (
    <>
      <Grid item xs={6} style={latLngStyle}>
        <BoundedTextField
          name="lat"
          label="Latitude *"
          val={lat}
          onSet={updateLocation}
        />
        <BoundedTextField
          name="lng"
          label="Longitude *"
          val={lng}
          onSet={updateLocation}
        />
      </Grid>
    </>
  );
};

export const Marker = ({ show, place }: any) => {
  const markerStyle = {
    border: "1px solid white",
    borderRadius: "50%",
    height: 10,
    width: 10,
    backgroundColor: "#2196f3",
    cursor: "pointer",
    zIndex: 10,
  };

  return (
    <>
      <div style={markerStyle} />
    </>
  );
};

export default SchoolCreate;
